import React, { createContext, useContext, useState, useCallback } from 'react';
import { Icon } from './Icon';
import styles from './Toast.module.css';

const ToastContext = createContext(null);

let toastIdSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(({ message, type = 'info', duration = 3500 }) => {
    const id = ++toastIdSeq;
    const newToast = { id, message, type };

    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const value = {
    toast,
    show: (msg, opts = {}) => toast({ message: msg, type: opts.variant || opts.type || 'info', duration: opts.duration }),
    success: (msg, dur) => toast({ message: msg, type: 'success', duration: dur }),
    error: (msg, dur) => toast({ message: msg, type: 'error', duration: dur }),
    info: (msg, dur) => toast({ message: msg, type: 'info', duration: dur }),
    removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.toastContainer} aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`${styles.toast} ${styles[t.type]}`}
            role="alert"
          >
            <span className={styles.icon}>
              {t.type === 'success' && <Icon name="check" size={16} />}
              {t.type === 'error' && <Icon name="alert" size={16} />}
              {t.type === 'info' && <Icon name="bell" size={16} />}
            </span>
            <span className={styles.message}>{t.message}</span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => removeToast(t.id)}
              aria-label="Dismiss toast"
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

export function Toast({ message, type = 'success', onClose }) {
  if (!message) return null;
  return (
    <div style={{ position: 'fixed', bottom: 'var(--space-6)', right: 'var(--space-6)', zIndex: 9999 }}>
      <div className={`${styles.toast} ${styles[type] || styles.success}`}>
        <span className={styles.icon}>
          <Icon name="check" size={16} />
        </span>
        <span className={styles.message}>{message}</span>
        {onClose && (
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Dismiss toast">
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
